import { useMutation } from "@tanstack/react-query";
import { use } from "react";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { useDeleteCardFromGlobalSearchCache } from "./useDeleteCardFromGlobalSearchCache.ts";
import { useDeleteCardFromProjectCache } from "./useDeleteCardFromProjectCache.ts";
import { useRemoveCardFromEditViewsCache } from "./useRemoveCardFromEditViewsCache.ts";

export const useDeleteSharedCard = (projectId: string) => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const deleteCardFromProjectCache = useDeleteCardFromProjectCache(projectId);
  const deleteCardFromGlobalSearchCache =
    useDeleteCardFromGlobalSearchCache(projectId);
  const removeCardFromEditViewsCache = useRemoveCardFromEditViewsCache();
  const { displayError, displaySuccess } = use(SnackbarContext);
  const { error, isPending, mutate, isError, reset } = useMutation({
    mutationFn: async (id: string): Promise<Response> => {
      const token = await getToken();
      const response = await fetch(
        `/api/projects/${projectId}/cards/${id}/share`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Removing the shared card failed");
      }

      return response;
    },
    mutationKey: ["deleteShareCard"],
    onSuccess: (_, id: string) => {
      deleteCardFromProjectCache(id);
      deleteCardFromGlobalSearchCache(id);
      removeCardFromEditViewsCache(id);
      displaySuccess(translator("sharedCardRemoved"));
    },
    onError: () => {
      displayError(translator("errorRemovingSharedCard"));
    },
  });

  return { mutate, isPending, error, isError, reset };
};
